import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { featureRoleAliases } from '@/utils/featureRoleAliases'
import { getRealmId, isOrgFlowRealm } from '@/utils/realmId'
import { ACCESS_LEVELS, type AccessLevel } from '@/composables/useAccessControl'
import { maxAccessIndexForSubject, hasSubjectPrefix } from '@/utils/accessOverview'
import type { AccessOverviewResponse } from '@/services/access.service'

// A FEAT_* string is always either `FEAT_<TYPE>_<LEVEL>` (e.g. FEAT_CORE_ADMIN) or a bare,
// levelless flag (e.g. FEAT_DRAWING, FEAT_3D-MODEL). Type names may themselves contain
// underscores (FEAT_CUSTOM_ATTRIBUTES_ADMIN), so only the LAST underscore-delimited segment is
// tried as a level -- if it isn't one of ACCESS_LEVELS, the whole remainder is the type and there
// is no level requirement (any grant at all counts).
function parseFeat(feat: string): { type: string; level: AccessLevel | null } {
  const withoutPrefix = feat.replace(/^FEAT_/, '')
  const splitIndex = withoutPrefix.lastIndexOf('_')
  const candidateLevel = splitIndex === -1 ? '' : withoutPrefix.slice(splitIndex + 1)

  if (ACCESS_LEVELS.includes(candidateLevel as AccessLevel)) {
    return { type: withoutPrefix.slice(0, splitIndex), level: candidateLevel as AccessLevel }
  }
  return { type: withoutPrefix, level: null }
}

export const useAccessStore = defineStore('access', () => {
  const featureAccess = ref<string[]>([])

  function setFeatureAccess(features: string[]) {
    featureAccess.value = features
  }

  // The org-flow realm's access-service grants (ZDB-7214) -- kept here, not read from
  // pinia/organization.ts, deliberately: this store is imported by pinia/features.ts, which is
  // part of the chain src/test.init.ts loads eagerly for every test, before per-test
  // `vi.mock('@/services/organizations.service' | '@/services/access.service', ...)` calls can
  // intercept anything. A static import of either store/service from here previously loaded the
  // REAL modules during that eager phase and silently broke those specs' mocks. Populated by
  // HomePage.vue's initializeOrgFlowFeatures(), same place featureAccess is populated.
  const accessOverview = ref<AccessOverviewResponse | undefined>(undefined)

  function setAccessOverview(overview: AccessOverviewResponse | undefined) {
    accessOverview.value = overview
  }

  // ZDB-7214: on the org-flow realm, a FEAT_* string is answered from the caller's real
  // access-service grants instead of the legacy JWT-role array -- see useAccess.ts, which is the
  // equivalent gate for code that isn't going through this store's has*Access getters. Legacy
  // per-customer realms are completely untouched by this branch.
  const has = (feat: string) => {
    if (!isOrgFlowRealm(getRealmId())) return featureAccess.value.includes(feat)

    const { type, level } = parseFeat(feat)
    const maxIndex = maxAccessIndexForSubject(accessOverview.value, type)
    if (!level) return maxIndex >= 0
    return ACCESS_LEVELS.indexOf(level) <= maxIndex
  }

  const has3dModelAccess = computed(() => has('FEAT_3D-MODEL'))
  const hasCoreAdminAccess = computed(() => has('FEAT_CORE_ADMIN'))
  const hasCoreCreatorAccess = computed(() => has('FEAT_CORE_ADMIN') || has('FEAT_CORE_CREATOR'))
  const hasCoreEditorAccess = computed(
    () => has('FEAT_CORE_ADMIN') || has('FEAT_CORE_CREATOR') || has('FEAT_CORE_EDITOR')
  )
  const hasCoreReaderAccess = computed(
    () =>
      has('FEAT_CORE_ADMIN') ||
      has('FEAT_CORE_CREATOR') ||
      has('FEAT_CORE_EDITOR') ||
      has('FEAT_CORE_READER')
  )
  const hasCustomAttributes = (level: string) =>
    featureRoleAliases('CUSTOM_ATTRIBUTES').some((key) => has(`FEAT_${key}_${level}`))
  const hasCustomAttributesAdminAccess = computed(() => hasCustomAttributes('ADMIN'))
  const hasCustomAttributesCreatorAccess = computed(
    () => hasCustomAttributes('ADMIN') || hasCustomAttributes('CREATOR')
  )
  const hasCustomAttributesEditorAccess = computed(
    () =>
      hasCustomAttributes('ADMIN') ||
      hasCustomAttributes('CREATOR') ||
      hasCustomAttributes('EDITOR')
  )
  const hasCustomAttributesReaderAccess = computed(
    () =>
      hasCustomAttributes('ADMIN') ||
      hasCustomAttributes('CREATOR') ||
      hasCustomAttributes('EDITOR') ||
      hasCustomAttributes('READER')
  )
  const hasDrawingAccess = computed(() => has('FEAT_DRAWING'))
  const hasDroneviewAccess = computed(() => has('FEAT_DRONE-VIEW'))
  const hasErrandAdminAccess = computed(() => has('FEAT_ERRAND_ADMIN'))
  const hasErrandCreatorAccess = computed(
    () => has('FEAT_ERRAND_ADMIN') || has('FEAT_ERRAND_CREATOR')
  )
  const hasErrandEditorAccess = computed(
    () => has('FEAT_ERRAND_ADMIN') || has('FEAT_ERRAND_CREATOR') || has('FEAT_ERRAND_EDITOR')
  )
  const hasErrandReaderAccess = computed(
    () =>
      has('FEAT_ERRAND_ADMIN') ||
      has('FEAT_ERRAND_CREATOR') ||
      has('FEAT_ERRAND_EDITOR') ||
      has('FEAT_ERRAND_READER')
  )
  const hasFileAdminAccess = computed(() => has('FEAT_FILE_ADMIN'))
  const hasFileCreatorAccess = computed(() => has('FEAT_FILE_ADMIN') || has('FEAT_FILE_CREATOR'))
  const hasFileEditorAccess = computed(
    () => has('FEAT_FILE_ADMIN') || has('FEAT_FILE_CREATOR') || has('FEAT_FILE_EDITOR')
  )
  const hasFileReaderAccess = computed(
    () =>
      has('FEAT_FILE_ADMIN') ||
      has('FEAT_FILE_CREATOR') ||
      has('FEAT_FILE_EDITOR') ||
      has('FEAT_FILE_READER')
  )
  const hasIbinderAccess = computed(
    () =>
      has('FEAT_IBINDER_ADMIN') ||
      has('FEAT_IBINDER_CREATOR') ||
      has('FEAT_IBINDER_EDITOR') ||
      has('FEAT_IBINDER_READER')
  )
  const hasIndoorviewAccess = computed(
    () => has('FEAT_INDOOR-VIEW_EDITOR') || has('FEAT_INDOOR-VIEW_READER')
  )
  const hasIndoorviewEditorAccess = computed(() => has('FEAT_INDOOR-VIEW_EDITOR'))
  const hasLineAdminAccess = computed(() => has('FEAT_LINE_ADMIN'))
  const hasLineCreatorAccess = computed(() => has('FEAT_LINE_ADMIN') || has('FEAT_LINE_CREATOR'))
  const hasLineEditorAccess = computed(
    () => has('FEAT_LINE_ADMIN') || has('FEAT_LINE_CREATOR') || has('FEAT_LINE_EDITOR')
  )
  const hasLineReaderAccess = computed(
    () =>
      has('FEAT_LINE_ADMIN') ||
      has('FEAT_LINE_CREATOR') ||
      has('FEAT_LINE_EDITOR') ||
      has('FEAT_LINE_READER')
  )
  const hasLinkAdminAccess = computed(() => has('FEAT_LINK_ADMIN'))
  const hasLinkCreatorAccess = computed(() => has('FEAT_LINK_ADMIN') || has('FEAT_LINK_CREATOR'))
  const hasLinkEditorAccess = computed(
    () => has('FEAT_LINK_ADMIN') || has('FEAT_LINK_CREATOR') || has('FEAT_LINK_EDITOR')
  )
  const hasLinkReaderAccess = computed(
    () =>
      has('FEAT_LINK_ADMIN') ||
      has('FEAT_LINK_CREATOR') ||
      has('FEAT_LINK_EDITOR') ||
      has('FEAT_LINK_READER')
  )
  const hasOrthophotoAccess = computed(() => has('FEAT_ORTHOPHOTO'))
  const hasPanoramaAccess = computed(() => has('FEAT_PANORAMA-VIEW'))
  const hasPoiCreatorAccess = computed(() => has('FEAT_CORE_ADMIN') || has('FEAT_CORE_CREATOR'))
  const hasSensorDataAccess = computed(() => has('FEAT_SENSOR-DATA'))
  const hasZoneAccess = computed(() => {
    if (!isOrgFlowRealm(getRealmId())) {
      return featureAccess.value.some((f) => f.startsWith('FEAT_ZONE'))
    }
    return hasSubjectPrefix(accessOverview.value, 'ZONE')
  })

  return {
    featureAccess,
    setFeatureAccess,
    accessOverview,
    setAccessOverview,
    has,
    has3dModelAccess,
    hasCoreAdminAccess,
    hasCoreCreatorAccess,
    hasCoreEditorAccess,
    hasCoreReaderAccess,
    hasCustomAttributesAdminAccess,
    hasCustomAttributesCreatorAccess,
    hasCustomAttributesEditorAccess,
    hasCustomAttributesReaderAccess,
    hasDrawingAccess,
    hasDroneviewAccess,
    hasErrandAdminAccess,
    hasErrandCreatorAccess,
    hasErrandEditorAccess,
    hasErrandReaderAccess,
    hasFileAdminAccess,
    hasFileCreatorAccess,
    hasFileEditorAccess,
    hasFileReaderAccess,
    hasIbinderAccess,
    hasIndoorviewAccess,
    hasIndoorviewEditorAccess,
    hasLineAdminAccess,
    hasLineCreatorAccess,
    hasLineEditorAccess,
    hasLineReaderAccess,
    hasLinkAdminAccess,
    hasLinkCreatorAccess,
    hasLinkEditorAccess,
    hasLinkReaderAccess,
    hasOrthophotoAccess,
    hasPanoramaAccess,
    hasPoiCreatorAccess,
    hasSensorDataAccess,
    hasZoneAccess
  }
})
